// Тач-управление (телефон/планшет): каждый палец — независимый указатель.
//
//   ┌──────────────────────────────┬──────────────────────────────┐
//   │ ☰ пауза                      │                              │
//   │                              │   #touch-look — обзор        │
//   │   #touch-move — плавающий    │   (свайп любым пальцем,      │
//   │   джойстик: тап в любом      │    начавшимся здесь)         │
//   │   месте зоны ставит базу,    │                    C ⤒ E  ● │
//   │   тянем ручку → 8 направлений│      кнопки поверх зоны ──┘ │
//   └──────────────────────────────┴──────────────────────────────┘
//
// Принципы «мультизадачности»:
//   - все обработчики ключуются по e.pointerId: джойстик, обзор, огонь и
//     кнопки живут одновременно (ходьба + обзор + огонь тремя пальцами);
//   - весь слой под touch-action: none + preventDefault на touch*: браузер не
//     стартует свой жест (пинч/скролл) и не шлёт pointercancel первому пальцу,
//     когда садится второй — раньше именно от этого «отпускалась» ходьба;
//   - кнопка огня — как в мобильных шутерах: удержание = автоогонь (в темпе
//     серверного кулдауна), протяжка по ней = прицеливание. Один палец
//     и стреляет, и поворачивает голову; второй — идёт джойстиком.
import { Btn, type Input } from "./input";

/** Радиус хода ручки джойстика (CSS px) — совпадает с CSS #touch-stick. */
const STICK_RADIUS = 56;
/** Мёртвая зона — доля радиуса, внутри которой движения нет. */
const STICK_DEADZONE = 0.28;
/** Радиус базы джойстика (половина ширины #touch-stick) — чтобы база не
 *  вылезала за край зоны при тапе у самого борта. */
const STICK_BASE_RADIUS = 66;

/** Отображаемое движение — цифровое (протокол: биты кнопок), поэтому вектор
 *  джойстика квантуем в 8 секторов по 45°. Экранный Y растёт вниз. */
export function stickMask(dx: number, dy: number): number {
  if (Math.hypot(dx, dy) < STICK_RADIUS * STICK_DEADZONE) return 0;
  const ang = Math.atan2(-dy, dx); // вверх = +π/2
  const sector = ((Math.round(ang / (Math.PI / 4)) % 8) + 8) % 8;
  switch (sector) {
    case 0: return Btn.Right;
    case 1: return Btn.Forward | Btn.Right;
    case 2: return Btn.Forward;
    case 3: return Btn.Forward | Btn.Left;
    case 4: return Btn.Left;
    case 5: return Btn.Back | Btn.Left;
    case 6: return Btn.Back;
    default: return Btn.Back | Btn.Right;
  }
}

export interface TouchControlsHandle {
  /** Сброс всех активных пальцев/визуала (уход в меню, смерть, потеря фокуса). */
  reset(): void;
}

const NO_POINTER = -1;

export function initTouchControls(input: Input, root: HTMLElement): TouchControlsHandle {
  const resets: Array<() => void> = [];

  // ── Гварды браузерных жестов на всём слое ──────────────────────────────
  // Пауза — обычная кнопка с click: её touch-события не трогаем, иначе
  // preventDefault на touchend убьёт синтез click.
  const isPause = (t: EventTarget | null): boolean =>
    !!(t as HTMLElement | null)?.closest?.("#touch-pause-btn");
  for (const type of ["touchstart", "touchmove", "touchend", "touchcancel"]) {
    root.addEventListener(type, (e) => {
      if (!isPause(e.target)) e.preventDefault();
    }, { passive: false });
  }

  // ── Обычные кнопки (присед / прыжок / действие): один палец на кнопку ──
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".touch-btn[data-dir]")) {
    const key = btn.dataset.dir;
    if (!key || key === "fire") continue;
    const mask = { jump: Btn.Jump, crouch: Btn.Crouch, use: Btn.Use }[key];
    if (!mask) continue;
    let activeId = NO_POINTER;
    const release = (): void => {
      activeId = NO_POINTER;
      btn.classList.remove("touch-btn--pressed");
      input.setTouchButton(mask, false);
    };
    btn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      if (activeId !== NO_POINTER) return; // второй палец на той же кнопке — игнор
      activeId = e.pointerId;
      try { btn.setPointerCapture(e.pointerId); } catch { /* */ }
      btn.classList.add("touch-btn--pressed");
      input.setTouchButton(mask, true);
    });
    const onUp = (e: PointerEvent): void => {
      if (e.pointerId !== activeId) return;
      try { btn.releasePointerCapture(e.pointerId); } catch { /* */ }
      release();
    };
    btn.addEventListener("pointerup", onUp);
    btn.addEventListener("pointercancel", onUp);
    btn.addEventListener("lostpointercapture", onUp);
    resets.push(release);
  }

  // ── Огонь: удержание = автоогонь, протяжка = прицеливание ──────────────
  const fireBtn = root.querySelector<HTMLButtonElement>(".touch-btn--fire");
  if (fireBtn) {
    let activeId = NO_POINTER;
    let lastX = 0, lastY = 0, lastT = 0;
    const release = (): void => {
      activeId = NO_POINTER;
      fireBtn.classList.remove("touch-btn--pressed");
      input.setTouchFireHeld(false);
    };
    fireBtn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      // Новый палец перехватывает кнопку: если отпускание прежнего потерялось
      // (iOS), кнопка не должна «залипнуть» навсегда.
      activeId = e.pointerId;
      try { fireBtn.setPointerCapture(e.pointerId); } catch { /* */ }
      lastX = e.clientX; lastY = e.clientY; lastT = e.timeStamp;
      fireBtn.classList.add("touch-btn--pressed");
      input.beginTouchLook();
      input.setTouchFireHeld(true);
    });
    fireBtn.addEventListener("pointermove", (e) => {
      if (e.pointerId !== activeId) return;
      const dx = e.clientX - lastX, dy = e.clientY - lastY, dt = e.timeStamp - lastT;
      lastX = e.clientX; lastY = e.clientY; lastT = e.timeStamp;
      input.addTouchLook(dx, dy, dt);
    });
    const onUp = (e: PointerEvent): void => {
      if (e.pointerId !== activeId) return;
      try { fireBtn.releasePointerCapture(e.pointerId); } catch { /* */ }
      release();
    };
    fireBtn.addEventListener("pointerup", onUp);
    fireBtn.addEventListener("pointercancel", onUp);
    fireBtn.addEventListener("lostpointercapture", onUp);
    window.addEventListener("pointerup", onUp, true);
    window.addEventListener("pointercancel", onUp, true);
    resets.push(release);
  }

  // ── Обзор: первый палец, севший в зону, ведёт камеру ───────────────────
  const lookEl = root.querySelector<HTMLElement>("#touch-look");
  if (lookEl) {
    let activeId = NO_POINTER;
    let lastX = 0, lastY = 0, lastT = 0;
    lookEl.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      // Новый палец всегда перехватывает обзор. Раньше второй палец
      // игнорировался, пока не придёт отпускание первого, — а iOS его иногда
      // теряет (системный жест, всплывшее окно), и голова «залипала».
      activeId = e.pointerId;
      try { lookEl.setPointerCapture(e.pointerId); } catch { /* */ }
      lastX = e.clientX; lastY = e.clientY; lastT = e.timeStamp;
      input.beginTouchLook();
    });
    lookEl.addEventListener("pointermove", (e) => {
      if (e.pointerId !== activeId) return;
      const dx = e.clientX - lastX, dy = e.clientY - lastY, dt = e.timeStamp - lastT;
      lastX = e.clientX; lastY = e.clientY; lastT = e.timeStamp;
      input.addTouchLook(dx, dy, dt);
    });
    const onUp = (e: PointerEvent): void => {
      if (e.pointerId === activeId) activeId = NO_POINTER;
    };
    lookEl.addEventListener("pointerup", onUp);
    lookEl.addEventListener("pointercancel", onUp);
    lookEl.addEventListener("lostpointercapture", onUp);
    window.addEventListener("pointerup", onUp, true);
    window.addEventListener("pointercancel", onUp, true);
    resets.push(() => { activeId = NO_POINTER; });
  }

  // ── Джойстик движения (плавающий) ──────────────────────────────────────
  const moveEl = root.querySelector<HTMLElement>("#touch-move");
  const stickEl = root.querySelector<HTMLElement>("#touch-stick");
  const knobEl = root.querySelector<HTMLElement>("#touch-stick-knob");
  if (moveEl && stickEl && knobEl) {
    let activeId = NO_POINTER;
    let cx = 0, cy = 0; // центр базы (clientX/Y)
    let lastMask = 0;
    const hide = (): void => {
      activeId = NO_POINTER;
      stickEl.classList.add("hidden");
      knobEl.style.transform = "translate(0px, 0px)";
      if (lastMask !== 0) { lastMask = 0; }
      input.setTouchMove(0);
    };
    moveEl.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      // Новый палец перехватывает джойстик (база — под ним), см. обзор.
      activeId = e.pointerId;
      try { moveEl.setPointerCapture(e.pointerId); } catch { /* */ }
      // База — под пальцем, но целиком внутри зоны (у борта чуть сдвигаем).
      const r = moveEl.getBoundingClientRect();
      cx = Math.min(Math.max(e.clientX, r.left + STICK_BASE_RADIUS), r.right - STICK_BASE_RADIUS);
      cy = Math.min(Math.max(e.clientY, r.top + STICK_BASE_RADIUS), r.bottom - STICK_BASE_RADIUS);
      stickEl.style.left = `${cx - r.left}px`;
      stickEl.style.top = `${cy - r.top}px`;
      knobEl.style.transform = "translate(0px, 0px)";
      stickEl.classList.remove("hidden");
      lastMask = 0;
      input.setTouchMove(0);
    });
    moveEl.addEventListener("pointermove", (e) => {
      if (e.pointerId !== activeId) return;
      let dx = e.clientX - cx, dy = e.clientY - cy;
      const len = Math.hypot(dx, dy);
      if (len > STICK_RADIUS) { dx *= STICK_RADIUS / len; dy *= STICK_RADIUS / len; }
      knobEl.style.transform = `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px)`;
      const mask = stickMask(dx, dy);
      if (mask !== lastMask) {
        lastMask = mask;
        input.setTouchMove(mask);
      }
    });
    const onUp = (e: PointerEvent): void => {
      if (e.pointerId !== activeId) return;
      try { moveEl.releasePointerCapture(e.pointerId); } catch { /* */ }
      hide();
    };
    moveEl.addEventListener("pointerup", onUp);
    moveEl.addEventListener("pointercancel", onUp);
    moveEl.addEventListener("lostpointercapture", onUp);
    window.addEventListener("pointerup", onUp, true);
    window.addEventListener("pointercancel", onUp, true);
    resets.push(hide);
  }

  // ── Страховка: пальцев на экране нет — ничего не может быть зажато ─────
  // (отпускание потерялось, окно ушло в фон, всплыл системный жест).
  const resetAll = (): void => { for (const r of resets) r(); };
  const onTouchEnd = (e: TouchEvent): void => { if (e.touches.length === 0) resetAll(); };
  window.addEventListener("touchend", onTouchEnd, { capture: true, passive: true });
  window.addEventListener("touchcancel", onTouchEnd, { capture: true, passive: true });
  window.addEventListener("blur", resetAll);
  document.addEventListener("visibilitychange", () => { if (document.hidden) resetAll(); });

  return {
    reset(): void {
      for (const r of resets) r();
    },
  };
}
